import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { getToken } from '../services/otpAuthService';
import { sessioneDiDipendente } from '../utils/sezioniDipendente';
import { useToast } from '../contexts/ToastContext';

const SOCKET_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/graphql', '').replace('/api', '')
  : 'http://localhost:5000';

interface VoucherMessage {
  id: string;
  voucherId: string;
  mittente?: {
    _id: string;
    nominativo: string;
    rappresentante?: string;
    codice: string;
  };
  mittenteAdmin?: string;
  mittenteAdminNome?: string;
  destinatario: {
    _id: string;
    nominativo: string;
    rappresentante?: string;
    codice: string;
  };
  testo: string;
  isAdmin: boolean;
  createdAt: string;
}

interface OnlineUser {
  id: string;
  name: string;
  codice?: string;
  isAdmin: boolean;
}

interface VoucherUpdateEvent {
  voucherId: string;
  type: string;
  message: string;
}

interface UseVoucherSocketOptions {
  voucherId: string;
  onNewMessage?: (message: VoucherMessage) => void;
  onUsersChange?: (users: OnlineUser[]) => void;
  onVoucherUpdated?: (event: VoucherUpdateEvent) => void;
  enabled?: boolean;
}

// Helper per verificare se l'utente è admin (JWT) o associato (OTP)
const isAdminUser = (): boolean => {
  return !!localStorage.getItem('accessToken');
};

const getAdminToken = (): string | null => {
  return localStorage.getItem('accessToken');
};

export const useVoucherSocket = ({
  voucherId,
  onNewMessage,
  onUsersChange,
  onVoucherUpdated,
  enabled = true
}: UseVoucherSocketOptions) => {
  const toast = useToast();
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [typingUsers, setTypingUsers] = useState<{ id: string; name: string }[]>([]);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Ref per callback onVoucherUpdated (evita stale closure nel socket listener)
  const onVoucherUpdatedRef = useRef(onVoucherUpdated);
  onVoucherUpdatedRef.current = onVoucherUpdated;

  // Connessione Socket.IO
  useEffect(() => {
    if (!enabled || !voucherId) return;

    const isAdmin = isAdminUser();
    const token = isAdmin ? getAdminToken() : getToken();

    if (!token) {
      console.warn('Token non disponibile per socket voucher');
      return;
    }
    // Il namespace accetta solo il token del titolare: il dipendente non apre il socket (la pagina va col REST)
    if (!isAdmin && sessioneDiDipendente()) return;

    // Connetti al namespace /voucher
    socketRef.current = io(`${SOCKET_URL}/voucher`, {
      auth: {
        token,
        tokenType: isAdmin ? 'admin' : 'associate'
      },
      transports: ['websocket', 'polling']
    });

    socketRef.current.on('connect', () => {
      console.log('🔌 Voucher socket connesso');
      setConnected(true);

      // Join alla stanza del voucher
      socketRef.current?.emit('join_voucher_room', { voucherId });
    });

    socketRef.current.on('disconnect', () => {
      console.log('🔌 Voucher socket disconnesso');
      setConnected(false);
    });

    socketRef.current.on('error', (err: { message: string }) => {
      console.error('Voucher socket error:', err.message);
      toast.error(err.message || 'Errore connessione chat');
    });

    // Eventi messaggi
    socketRef.current.on('voucher_new_message', (msg: VoucherMessage) => {
      onNewMessage?.(msg);
    });

    // Eventi utenti online
    socketRef.current.on('voucher_online_users', ({ users }: { users: OnlineUser[] }) => {
      setOnlineUsers(users);
      onUsersChange?.(users);
    });

    socketRef.current.on('voucher_user_joined', ({ user }: { user: OnlineUser }) => {
      setOnlineUsers(prev => {
        if (prev.some(u => u.id === user.id)) return prev;
        return [...prev, user];
      });
    });

    socketRef.current.on('voucher_user_left', ({ userId }: { userId: string }) => {
      setOnlineUsers(prev => prev.filter(u => u.id !== userId));
    });

    // Eventi typing - usiamo Map per tracciare userId -> name
    socketRef.current.on('voucher_user_typing', ({ user }: { user: OnlineUser }) => {
      setTypingUsers(prev => {
        if (prev.some(t => t.id === user.id)) return prev;
        return [...prev, { id: user.id, name: user.name }];
      });
    });

    socketRef.current.on('voucher_user_stopped_typing', ({ userId }: { userId: string }) => {
      setTypingUsers(prev => prev.filter(t => t.id !== userId));
    });

    // Evento aggiornamento voucher (upload PDF, conferma, ecc.)
    socketRef.current.on('voucher_updated', (event: VoucherUpdateEvent) => {
      onVoucherUpdatedRef.current?.(event);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.emit('leave_voucher_room', { voucherId });
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [voucherId, enabled]);

  // Funzione per inviare messaggio
  const sendMessage = useCallback((content: string) => {
    if (!socketRef.current || !connected || !content.trim()) {
      return false;
    }

    socketRef.current.emit('send_voucher_message', {
      voucherId,
      content: content.trim()
    });

    return true;
  }, [voucherId, connected]);

  // Funzione per indicare che sta scrivendo
  const startTyping = useCallback(() => {
    if (!socketRef.current || !connected) return;

    socketRef.current.emit('voucher_typing_start', { voucherId });

    // Auto-stop dopo 2 secondi
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      socketRef.current?.emit('voucher_typing_stop', { voucherId });
    }, 2000);
  }, [voucherId, connected]);

  // Funzione per smettere di indicare che sta scrivendo
  const stopTyping = useCallback(() => {
    if (!socketRef.current || !connected) return;

    socketRef.current.emit('voucher_typing_stop', { voucherId });
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  }, [voucherId, connected]);

  // Funzione per marcare messaggi come letti
  const markAsRead = useCallback(() => {
    if (!socketRef.current || !connected) return;

    socketRef.current.emit('voucher_mark_read', { voucherId });
  }, [voucherId, connected]);

  return {
    connected,
    onlineUsers,
    typingUsers,
    sendMessage,
    startTyping,
    stopTyping,
    markAsRead
  };
};

export default useVoucherSocket;

